import * as React from 'react';
import { observer } from 'mobx-react';

import { PersonInfoModel, PersonModel } from '@app/Models/WebApiModels';
import { PersonInfo } from '@app/AppConstants/AppConstants';

import { Icon } from '@app/Components/Icon';
import { License } from '@app/Components/License';
import DateTimeService from '@app/Services/DateTimeService';
import PersonCommentDialog from './Dialogs/PersonCommentDialog';
import { IPersonStore } from './VirtualizedRoster';
import { modalService } from './Modal';
import { ToolTipItem } from './ToolTipItem';
import Comments from '@app/Pages/Home/Components/Comments';
import HistorizationStore from '@app/Stores/HistorizationStore';
import { applyDecorators } from "@app/Helpers/Decorator";

type PersonModelDto = Omit<PersonModel, 'isActive'>;

export type PersonListRenderExtraFiguresCallback = (person: PersonModelDto, personInfo: PersonInfoModel | null) => JSX.Element | null;
export type PersonListRenderExtraDataCallback = (person: PersonModelDto, personInfo: PersonInfoModel | null) => JSX.Element | null;
export type LicensesClickHandler = (person: PersonModelDto) => void;

type PersonsListItemProps = {
    personStore?: IPersonStore;
    person: PersonModelDto | null;
    personInfo?: PersonInfoModel | null;
    className?: string;
    referenceDate?: Date | null;
    historizationStore?: HistorizationStore;
    renderExtraFiguresCallback?: PersonListRenderExtraFiguresCallback;
    renderExtraDataCallback?: PersonListRenderExtraDataCallback;
    onLicensesClick?: LicensesClickHandler;
};

@observer
export class PersonsListItem extends React.Component<PersonsListItemProps> {
    
    constructor (props:PersonsListItemProps) {
        super(props);
        applyDecorators(this);
    }
    
    public render () {
        const { className, personInfo, referenceDate, renderExtraFiguresCallback, renderExtraDataCallback, onLicensesClick } = this.props;
        let person = this.props.person;

        if (!person && personInfo) {
            person = new PersonModel({
                id: personInfo.id,
                code: personInfo.code,
                firstName: personInfo.firstName || '',
                lastName: personInfo.lastName || '',
                email: '',
                phoneNumber: '',
                isActive: true
            });
        }

        if (!person) {
            throw new Error('person property is required if personInfo is not defined');
        }

        let contract = '';
        let personFunction = '';
        let personPlanningGroup = '';
        let experienceDisplayName = '';
        let experienceCodeName = '';
        const tooltipTargetId = person.code + '_' + person.id.split('-')[0];
        const targetCommentId = tooltipTargetId + '_comment';

        const hasComments = personInfo && personInfo.hasComments; // todo: add value from model
        if (personInfo) {
            if (personInfo.function) {
                personFunction = personInfo.function.name;
            }
            personPlanningGroup = personInfo.function?.planningGroup || '';
            if (personInfo.personContract) {
                contract = personInfo.personContract.actualContractPartTimePercentage ? Math.round(personInfo.personContract.actualContractPartTimePercentage * 100 * 100) / 100 + '%' : personInfo.personContract.actualContractType;

                if (personInfo.personContract.actualContractType === 'Freelance') {
                    experienceCodeName = 'freelancer';
                }
                if (personInfo.personContract.actualContractPartTimePercentage && personInfo.personContract.actualContractPartTimePercentage < 1) {
                    experienceCodeName = 'part-time';
                }
            }
            if (personInfo.experience) {
                experienceDisplayName = personInfo.experience.displayName;
                experienceCodeName = personInfo.experience.codeName.toLowerCase();
            }
        }

        let medicalRestrictionClassNames = '';
        if (referenceDate) {
            if (personInfo && personInfo.hasMedicalRestriction) medicalRestrictionClassNames += 'warningRed ';
            if (personInfo && personInfo.birthDate) {
                const age = DateTimeService.calculateAge(personInfo.birthDate, referenceDate);
                if (age >= PersonInfo.Age) medicalRestrictionClassNames += 'warningOrange ';
            }
        }

        const personName = `${person.firstName} ${person.lastName}`;

        const licenseBlock = () => {
            return personInfo && personInfo.personLicenses && personInfo.function && <License licenses={personInfo.personLicenses} currentFunction={personInfo.function} targetId={tooltipTargetId} selectedDate={referenceDate} />;
        };
        return (
            <>
                <div id={tooltipTargetId} className={'person-grid-person' + (className || '') + ' ' + experienceCodeName + ' ' + medicalRestrictionClassNames} key={person.id}>
                    <div className="person-grid-person-info">
                        <span className="person-grid-person-code">
                            {person.code}
                            <span className="person-grid-person-experience">{experienceDisplayName || personPlanningGroup || personFunction}</span>
                        </span>
                        <span id={targetCommentId} className="person-grid-person-comment" style={{ color: hasComments ? 'red' : '' }} onClick={this._onCommentClick}>
                            <Icon name="comment" className="person-comment-icon" noSolid={!hasComments} />
                            {hasComments && <ToolTipItem text={<Comments personId={person.id} date={referenceDate} />} className="tooltip-comments" targetId={targetCommentId} placement="right" />}
                        </span>
                        <span className="person-grid-person-plan">{contract}</span>
                    </div>
                    <div className="person-grid-person-crew">{onLicensesClick && person ? <a onClick={() => person && onLicensesClick(person)}>{licenseBlock()}</a> : licenseBlock()}</div>
                    <div className="person-grid-person-name">{personName}</div>
                    {className && className.indexOf('selected') > 0 && <span className="marker" />}
                    {renderExtraDataCallback && renderExtraDataCallback(person, personInfo || null)}
                </div>
                {renderExtraFiguresCallback && renderExtraFiguresCallback(person, personInfo || null)}
            </>
        );
    }

    private _onCommentClick = () => {
        const { personStore, personInfo, referenceDate, historizationStore } = this.props;
        if (!personStore || !personInfo || !referenceDate) return;

        void modalService.show(
            PersonCommentDialog,
            {
                personId: personInfo.id,
                date: referenceDate,
                personStore: personStore,
                historizationStore: historizationStore
            },
            {
                title: `Comments / ${personInfo.code} - ${personInfo.firstName} ${personInfo.lastName}`
            }
        );
    };
}